import { create } from "zustand";

/**
 * Sudo elevation store (DEV-FA4).
 *
 * Sensitive actions (revoke session, disable MFA, pair a POS device, change
 * override thresholds…) require a recent password re-confirmation — a "sudo"
 * window. The backend tracks the real window on the session and returns
 * `403 { error: "sudo_required" }` for @Sudo-guarded routes until the session
 * is elevated via `POST /v1/auth/sudo`.
 *
 * This store coordinates the single shared <SudoModal/> with two entry points:
 *  - Reactive: the axios interceptor calls {@link requestElevation} when a
 *    request fails with `sudo_required`, then retries on success.
 *  - Proactive: components call {@link requestElevation} via the
 *    `useSudoElevation` hook BEFORE a sensitive action so the user confirms up
 *    front rather than after a failed mutation.
 *
 * Concurrent callers share ONE modal: every waiter is queued and settled
 * together when the user succeeds or cancels. `elevatedUntil` is a client-side
 * UX hint only — if it drifts from the server window the worst case is a
 * `sudo_required` 403 that simply re-opens the modal, so the backend stays
 * authoritative.
 */
interface SudoWaiter {
  resolve: (elevated: boolean) => void;
}

interface SudoState {
  /** Whether the shared sudo modal is currently shown. */
  isOpen: boolean;
  /** Epoch ms until which the client believes sudo is elevated (UX hint only). */
  elevatedUntil: number | null;
  /** Callers awaiting the in-flight elevation. */
  _waiters: SudoWaiter[];
  /**
   * Ensure the session is sudo-elevated. Resolves `true` immediately if still
   * inside the client-tracked window (no modal); otherwise opens the modal
   * (joining any in-flight prompt) and resolves once the user confirms (`true`)
   * or cancels (`false`).
   */
  requestElevation: () => Promise<boolean>;
  /** Record a successful elevation. Called by SudoModal after POST /auth/sudo. */
  markElevated: (expiresInSeconds: number) => void;
  /** Settle every queued waiter (success or cancel) and close the modal. */
  settle: (elevated: boolean) => void;
  /** True while the client-tracked sudo window is still open. */
  isElevated: () => boolean;
  /** Whole seconds remaining in the client-tracked window (0 when expired). */
  timeRemaining: () => number;
}

export const useSudoStore = create<SudoState>((set, get) => ({
  isOpen: false,
  elevatedUntil: null,
  _waiters: [],

  isElevated: () => {
    const until = get().elevatedUntil;
    return until !== null && until > Date.now();
  },

  timeRemaining: () => {
    const until = get().elevatedUntil;
    if (until === null) return 0;
    return Math.max(0, Math.round((until - Date.now()) / 1000));
  },

  requestElevation: () => {
    if (get().isElevated()) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      set((s) => ({ isOpen: true, _waiters: [...s._waiters, { resolve }] }));
    });
  },

  markElevated: (expiresInSeconds: number) => {
    set({ elevatedUntil: Date.now() + expiresInSeconds * 1000 });
  },

  settle: (elevated: boolean) => {
    const { _waiters } = get();
    _waiters.forEach((w) => w.resolve(elevated));
    set({ isOpen: false, _waiters: [] });
  },
}));
